import { assertValue } from '@libs/utils';

import {
  TileSelectionType,
  isCurrentMonthAvailableDayTile,
  isCurrentMonthTile,
} from '../models';
import type {
  CurrentMonthAlternativeProductDayTile,
  CurrentMonthAvailableDayTile,
  CurrentMonthLoaderDayTile,
  CurrentMonthPastDayTile,
  CurrentMonthTile,
  CurrentMonthUnavailableDayTile,
  PreviousMonthDayTile,
  PriceCalendarTile,
  WeekDayTile,
} from '../models';

// NOTE: This class makes it easy to create calendar view model by encapsulating management of cursor over matrix.
// This means:
// * class instance is **single** use only
// * tiles have to be set **in order**: week days -> previous month -> current month
export class PriceCalendarState {
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  private static daysInAWeek = 7;
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  private static shortMonthWeeks = 6;
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  private static longMonthWeeks = 7;
  private currentWeekIndex = 1;
  private calendarMatrix: PriceCalendarTile[][] = [[], [], [], [], [], [], []];

  public getCalendarMatrix(): PriceCalendarTile[][] {
    return this.calendarMatrix;
  }

  public getCurrentMonthTiles(): CurrentMonthTile[] {
    return this.calendarMatrix.reduce<CurrentMonthTile[]>(
      (acc: CurrentMonthTile[], item: PriceCalendarTile[]) => {
        item.forEach((tile) => {
          if (isCurrentMonthTile(tile)) {
            acc.push(tile);
          }
        });
        return acc;
      },
      []
    );
  }

  public anyResultsLoaded(): boolean {
    const firstRowWithData = this.calendarMatrix[1];
    assertValue(
      firstRowWithData,
      'priceCalendarState.ts > anyResultsLoaded > firstRowWithData'
    );

    return firstRowWithData.length > 0;
  }

  public getTotalTilesInColumn(): number {
    const lastRow = this.calendarMatrix[6];
    assertValue(lastRow, 'priceCalendarState.ts > setWeekDayTiles > lastRow');
    return lastRow.length > 0
      ? PriceCalendarState.longMonthWeeks
      : PriceCalendarState.shortMonthWeeks;
  }

  public getSelectedOrSuggestedDayIndexFromAvailableDaysOnly(): number {
    const allTiles = this.calendarMatrix.flat();

    const currentAvailableTiles = allTiles.filter(
      isCurrentMonthAvailableDayTile
    );

    const selectedTile = currentAvailableTiles.find(
      (tile) => tile.selection === TileSelectionType.Selected
    );

    if (selectedTile) {
      return selectedTile.date.getDate();
    }

    const suggestedTile = currentAvailableTiles.find(
      (tile) => tile.selection === TileSelectionType.Suggested
    );

    if (suggestedTile) {
      return suggestedTile.date.getDate();
    }

    return 0;
  }

  public setWeekDayTiles(tiles: WeekDayTile[]): void {
    assertValue(
      this.calendarMatrix[0],
      'priceCalendarState.ts > setWeekDayTiles > this.calendarMatrix[0]'
    );
    this.calendarMatrix[0] = this.calendarMatrix[0].concat(tiles);
  }

  public setPreviousMonthTiles(tiles: PreviousMonthDayTile[]): void {
    assertValue(
      this.calendarMatrix[1],
      'priceCalendarState.ts > setCurrentMonthTile > currentWeek 1'
    );
    this.calendarMatrix[1] = this.calendarMatrix[1].concat(tiles);
  }

  public setCurrentMonthTile(
    tile:
      | CurrentMonthPastDayTile
      | CurrentMonthLoaderDayTile
      | CurrentMonthAvailableDayTile
      | CurrentMonthAlternativeProductDayTile
      | CurrentMonthUnavailableDayTile
  ): void {
    this.currentWeek.push(tile);
  }

  private get currentWeek(): PriceCalendarTile[] {
    let currentWeek = this.calendarMatrix[this.currentWeekIndex];

    assertValue(
      currentWeek,
      'priceCalendarState.ts > setCurrentMonthTile > currentWeek 1'
    );

    if (currentWeek.length === PriceCalendarState.daysInAWeek) {
      this.currentWeekIndex += 1;
    }
    currentWeek = this.calendarMatrix[this.currentWeekIndex];

    assertValue(
      currentWeek,
      'priceCalendarState.ts > setCurrentMonthTile > currentWeek 2'
    );

    return currentWeek;
  }
}
